import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Search,
  Home,
  FileText,
  Camera,
  Calendar as CalendarIcon,
  Bell,
  CreditCard,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import SearchPalette from './SearchPalette';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Home', icon: Home, testid: 'nav-home' },
  { path: '/letters', label: 'Write', icon: FileText, testid: 'nav-write' },
  { path: '/photos', label: 'Photos', icon: Camera, testid: 'nav-photos' },
  { path: '/calendar', label: 'Calendar', icon: CalendarIcon, testid: 'nav-calendar' },
];

export default function AppHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out');
    navigate('/');
  };

  const handleBillingPortal = async () => {
    try {
      const { data } = await axios.post(`${API}/subscription/portal`, {}, { withCredentials: true });
      window.location.href = data.url;
    } catch {
      navigate('/pricing');
    }
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <>
      <header className="border-b border-[#E5E0D8] bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center gap-3">
          <Link to="/dashboard" className="flex-shrink-0">
            <h1 className="text-xl md:text-2xl font-light tracking-tight" style={{fontFamily: 'Spectral, serif'}}>
              Stable Stationary
            </h1>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1 ml-6">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link key={item.path} to={item.path}>
                  <button
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                      active ? 'bg-[#F5EBE0] text-[#B94A3E]' : 'text-[#737373] hover:text-[#1A1A1A] hover:bg-[#F9F7F3]'
                    }`}
                    data-testid={item.testid}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                    {item.label}
                  </button>
                </Link>
              );
            })}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            {/* Search - always visible */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSearchOpen(true)}
              className="border-[#E5E0D8] gap-2 min-w-[44px] md:min-w-[200px] justify-start text-[#737373] hover:text-[#1A1A1A]"
              data-testid="search-open-btn"
            >
              <Search className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm hidden md:inline">Search anything...</span>
              <kbd className="ml-auto text-xs bg-[#F9F7F3] border border-[#E5E0D8] rounded px-1.5 py-0.5 hidden md:inline">⌘K</kbd>
            </Button>

            {/* Desktop secondary actions */}
            <div className="hidden md:flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={handleBillingPortal} data-testid="nav-billing" title="Billing">
                <CreditCard className="w-4 h-4" strokeWidth={1.5} />
              </Button>
              <Link to="/settings">
                <Button variant="ghost" size="sm" data-testid="nav-settings" title="Settings">
                  <Bell className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </Link>
              {user?.role === 'admin' && (
                <Link to="/admin">
                  <Button variant="ghost" size="sm" data-testid="nav-admin" title="Admin">
                    <SettingsIcon className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </Link>
              )}
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="nav-logout" title="Logout">
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            </div>

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile nav drawer */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-[#E5E0D8] bg-white" data-testid="mobile-menu">
            <nav className="px-4 py-2 flex flex-col">
              {NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`px-3 py-3 rounded-md text-sm font-medium flex items-center gap-3 ${
                      active ? 'bg-[#F5EBE0] text-[#B94A3E]' : 'text-[#1A1A1A] hover:bg-[#F9F7F3]'
                    }`}
                    data-testid={`mobile-${item.testid}`}
                  >
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                    {item.label}
                  </Link>
                );
              })}
              <div className="border-t border-[#E5E0D8] my-2" />
              <Link to="/settings" onClick={() => setMobileMenuOpen(false)} className="px-3 py-3 rounded-md text-sm font-medium flex items-center gap-3 text-[#1A1A1A] hover:bg-[#F9F7F3]">
                <Bell className="w-5 h-5" strokeWidth={1.5} />
                Settings
              </Link>
              <button onClick={handleBillingPortal} className="px-3 py-3 rounded-md text-sm font-medium flex items-center gap-3 text-[#1A1A1A] hover:bg-[#F9F7F3] text-left">
                <CreditCard className="w-5 h-5" strokeWidth={1.5} />
                Billing
              </button>
              {user?.role === 'admin' && (
                <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="px-3 py-3 rounded-md text-sm font-medium flex items-center gap-3 text-[#1A1A1A] hover:bg-[#F9F7F3]">
                  <SettingsIcon className="w-5 h-5" strokeWidth={1.5} />
                  Admin
                </Link>
              )}
              <button onClick={handleLogout} className="px-3 py-3 rounded-md text-sm font-medium flex items-center gap-3 text-red-600 hover:bg-red-50 text-left">
                <LogOut className="w-5 h-5" strokeWidth={1.5} />
                Logout
              </button>
            </nav>
          </div>
        )}
      </header>

      <SearchPalette open={searchOpen} onOpenChange={setSearchOpen} isAdmin={user?.role === 'admin'} />
    </>
  );
}
